import React from 'react';

const GOLD = '#D4A417';
const BG = '#13161e';
const BORDER = '#252a38';
const ACTIVE_TINT = 'rgba(212,164,23,0.12)';
const FUNCTION_TABS = ['settings', 'integrations', 'faq'];

// Tool nav items, top to bottom.
const NAV_ITEMS = [
  { id: 'spec-check', icon: '📋', label: 'Spec Check', featureKey: 'specCheck' },
  { id: 'spec-builder', icon: '🏗️', label: 'Spec Builder', featureKey: 'specBuilder' },
  { id: 'spec-update', icon: '🔄', label: 'Spec Update', featureKey: 'specUpdate' },
  { id: 'rfi-check', icon: '❓', label: 'RFI Check', featureKey: 'rfiCheck' },
  { id: 'shop-drawing-check', icon: '🔧', label: 'Package Check', chip: 'new', featureKey: 'shopDrawingCheck' },
  { id: 'design-vs-shop', icon: '📐', label: 'Design vs. Shop', featureKey: 'designVsShopDrawing' },
  { id: 'drawing-compare', icon: '⚖️', label: 'Drawing Compare', featureKey: 'drawingCompare' },
  { id: 'track-changes', icon: '📝', label: 'Track Changes', featureKey: 'trackChanges' },
  { id: 'general', icon: '💬', label: 'General', featureKey: 'generalChat' },
    { id: 'integrations', icon: '🔗', label: 'Integrations', featureKey: 'integrations' },
  { id: 'settings', icon: '⚙️', label: 'Settings', featureKey: null },
  { id: 'faq', icon: '❔', label: 'Help & FAQ', featureKey: null },
];

// Doc types that are spec/RFI-like get a green dot; drawings get a blue dot.
function dotColorForDocType(docType) {
  const t = (docType || '').toLowerCase();
  if (t.includes('drawing')) return '#3b82f6'; // blue
  return '#22c55e'; // green — spec / RFI / everything else
}

const sectionLabelStyle = {
  fontSize: '10px',
  fontWeight: '700',
  letterSpacing: '1px',
  color: '#5b627a',
  textTransform: 'uppercase',
  padding: '0 16px',
  marginBottom: '8px',
};

function LeftNav({ activeTab, onTabChange, cachedDocs, usageCount, usageLimit, activeMode, vaultDrawings, onToolSelect, getFlag }) {
  const docs = Array.isArray(cachedDocs) ? cachedDocs : [];
  const visibleItems = NAV_ITEMS.filter(item => {
    if (!item.featureKey) return true;
    const f = getFlag ? getFlag(item.featureKey) : { visible: true };
    return f.visible !== false;
  });
  const count = Number(usageCount) || 0;
  const limit = Number(usageLimit) || 0;
  const pct = limit > 0 ? Math.min(100, Math.round((count / limit) * 100)) : 0;

return (
    <div
      style={{
        position: 'fixed',
        top: '52px',
        left: 0,
        width: '220px',
        height: 'calc(100vh - 52px)',
        background: BG,
        borderRight: `1px solid ${BORDER}`,
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'sans-serif',
        boxSizing: 'border-box',
        zIndex: 900,
      }}
    >
      <style>{`
        .zt-leftnav-scroll::-webkit-scrollbar { width: 4px; }
        .zt-leftnav-scroll::-webkit-scrollbar-track { background: transparent; }
        .zt-leftnav-scroll::-webkit-scrollbar-thumb { background: #252a38; border-radius: 4px; }
        .zt-leftnav-scroll::-webkit-scrollbar-thumb:hover { background: #D4A417; }
      `}</style>
      {/* Tools section */}
      <div style={{ padding: '16px 0 8px' }}>
        <div style={sectionLabelStyle}>Tools</div>
        <nav>
          {visibleItems.map((item) => {
            const isActive = activeTab === item.id;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => {
                  onTabChange && onTabChange(item.id);
                  if (activeMode === 'manager' && onToolSelect && !FUNCTION_TABS.includes(item.id)) {
                    onToolSelect(item.label);
                  }
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  width: '100%',
                  border: 'none',
                  borderLeft: `3px solid ${isActive ? GOLD : 'transparent'}`,
                  background: isActive ? ACTIVE_TINT : 'transparent',
                  color: isActive ? '#f3f4f6' : '#9ca3af',
                  padding: '9px 16px',
                  fontSize: '13px',
                  fontWeight: isActive ? '600' : '500',
                  fontFamily: 'sans-serif',
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                <span style={{ fontSize: '15px', lineHeight: 1 }}>{item.icon}</span>
                <span style={{ flex: 1 }}>{item.label}</span>
                {item.chip && (
                  <span
                    style={{
                      fontSize: '9px',
                      fontWeight: '700',
                      letterSpacing: '0.5px',
                      textTransform: 'uppercase',
                      color: '#13161e',
                      background: GOLD,
                      borderRadius: '8px',
                      padding: '2px 6px',
                    }}
                  >
                    {item.chip}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Divider */}
      <div style={{ height: '1px', background: BORDER, margin: '8px 0' }} />

      {/* Cached Project Docs section */}
      <div style={{ padding: '8px 0' }}>
        <div style={sectionLabelStyle}>Cached Docs</div>
        {docs.length === 0 ? (
          <div style={{ padding: '4px 16px', fontSize: '12px', color: '#4b5168', fontStyle: 'italic' }}>
            No cached docs yet
          </div>
        ) : (
<ul style={{ 
  listStyle: 'none', margin: 0, padding: 0,
  maxHeight: '180px', overflowY: 'auto',
  scrollbarWidth: 'thin', scrollbarColor: '#252a38 transparent'
}} className="zt-leftnav-scroll">
  {[...docs].sort((a, b) => {
    const aTime = new Date(a.lastAccessedAt || a.uploadedAt || a.createdAt || 0).getTime();
    const bTime = new Date(b.lastAccessedAt || b.uploadedAt || b.createdAt || 0).getTime();
    return bTime - aTime;
  }).map((doc, i) => (
              <li
                key={(doc && doc.name) ? `${doc.name}-${i}` : i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '6px 16px',
                  fontSize: '12px',
                  color: '#9ca3af',
                }}
              >
                <span
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: dotColorForDocType(doc && doc.docType),
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={(doc && doc.name) || ''}
                >
                  {(doc && doc.name) || 'Untitled'}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Drawing Vault section */}
      {vaultDrawings?.length > 0 && (
        <div style={{ padding: '12px 16px 0' }}>
          <div style={{ fontSize: '10px', fontWeight: '700', color: '#5b627a', letterSpacing: '0.08em', marginBottom: '8px' }}>
            DRAWING VAULT
          </div>
          {vaultDrawings.map((d, i) => (
            <div key={d._id || i} style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '3px 0', fontSize: '11px', color: '#9ca3af'
            }}>
              <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#3b82f6', flexShrink: 0 }} />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {d.fileName}{d.sheetNumber ? ` (${d.sheetNumber})` : ''}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Bottom — monthly usage bar */}
      <div style={{ padding: '12px 16px', borderTop: `1px solid ${BORDER}` }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '6px',
          }}
        >
          <span style={{ fontSize: '11px', fontWeight: '600', color: '#9ca3af' }}>Monthly Usage</span>
          <span style={{ fontSize: '11px', color: '#7a8099' }}>
            {count} / {limit}
          </span>
        </div>
        <div
          style={{
            height: '6px',
            background: '#252a38',
            borderRadius: '3px',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              width: `${pct}%`,
              height: '100%',
              background: GOLD,
              borderRadius: '3px',
            }}
          />
        </div>
      </div>
    </div>
  );
}

export default LeftNav;
